/**
 * @file SetNotificationPreferencesForm.js
 * @description This file contains the SetNotificationPreferencesForm component,
 * which allows users to set their notification preferences using radio buttons.
 */
import React, { useState, useEffect } from 'react';
import styles from './OnboardingPage.module.scss';
import Toast from '../ui/toasts/Toast';
import { NOTIFICATION_METHOD } from '../../utils/data/enums';
import { formatPhoneNumber } from '../../utils/utils';
import { getText } from '../../i18n';

const SetNotificationPreferencesForm = ({
    onSetNotificationPreferences,
    communicationPreferences,
    onboardingInfo,
    notificationPreferences,
}) => {
    const [selectedPreference, setSelectedPreference] = useState('');
    const [availableChannels, setAvailableChannels] = useState({
        originalEmail: '',
        modifiedEmail: '',
        originalPhone: '',
        modifiedPhone: '',
    });

    const { companyCode, isSelfRegistration, profileData } = onboardingInfo;

    useEffect(() => {
        const originalEmail = isSelfRegistration
            ? profileData?.email
            : onboardingInfo.userDetails?.email;

        const originalPhone = isSelfRegistration
            ? profileData?.mobilePhoneNumber
            : onboardingInfo.userDetails?.mobile_phone_number;

        const modifiedEmail = communicationPreferences?.email;
        const modifiedPhone = communicationPreferences?.mobilePhoneNumber;

        setAvailableChannels({
            originalEmail: originalEmail || '',
            modifiedEmail:
                modifiedEmail && modifiedEmail !== originalEmail
                    ? modifiedEmail
                    : '',
            originalPhone: originalPhone || '',
            modifiedPhone:
                modifiedPhone && modifiedPhone !== originalPhone
                    ? modifiedPhone
                    : '',
        });
    }, [
        communicationPreferences,
        onboardingInfo,
        isSelfRegistration,
        profileData,
    ]);

    useEffect(() => {
        if (notificationPreferences?.preferences) {
            const savedPreference = Object.keys(
                notificationPreferences.preferences,
            )[0];
            if (savedPreference) {
                setSelectedPreference(savedPreference);
            }
        }
    }, [notificationPreferences]);

    const handleRadioChange = event => {
        setSelectedPreference(event.target.value);
    };

    const handleSubmit = event => {
        event.preventDefault();
        if (!selectedPreference) {
            Toast.error(getText('msg_55ab02b516ce'));
            return;
        }

        let comm_preference;
        let selectedChannel;
        let notification_preferred_method_id;
        let username;

        switch (selectedPreference) {
            case 'originalEmail':
                comm_preference = 'email';
                selectedChannel = availableChannels.originalEmail;
                notification_preferred_method_id =
                    NOTIFICATION_METHOD.primaryEmail;
                username = availableChannels.originalEmail;
                break;
            case 'modifiedEmail':
                comm_preference = 'email';
                selectedChannel = availableChannels.modifiedEmail;
                notification_preferred_method_id =
                    NOTIFICATION_METHOD.optionalEmail;
                username = availableChannels.modifiedEmail;
                break;
            case 'originalPhone':
                comm_preference = 'sms';
                selectedChannel = availableChannels.originalPhone;
                notification_preferred_method_id =
                    NOTIFICATION_METHOD.primaryPhone;
                username =
                    availableChannels.originalEmail ||
                    availableChannels.originalPhone;
                break;
            case 'modifiedPhone':
                comm_preference = 'sms';
                selectedChannel = availableChannels.modifiedPhone;
                notification_preferred_method_id =
                    NOTIFICATION_METHOD.optionalPhone;
                username =
                    availableChannels.originalEmail ||
                    availableChannels.modifiedEmail ||
                    availableChannels.modifiedPhone;
                break;
            default:
                break;
        }

        const updatedCommunicationPreferences = {
            ...communicationPreferences,
            comm_preference,
            selectedChannel,
            notification_preferred_method_id,
            username,
            email:
                communicationPreferences?.email ||
                availableChannels.originalEmail ||
                '',
            mobilePhoneNumber:
                communicationPreferences?.mobilePhoneNumber ||
                availableChannels.originalPhone ||
                '',
        };

        onSetNotificationPreferences({
            communicationPreferences: updatedCommunicationPreferences,
            preferences: { [selectedPreference]: true },
        });
    };

    const renderRadioButton = (id, name, label) => {
        if (!label) {
            return null;
        }

        return (
            <div className={`${styles.radioItem} form-check`}>
                <input
                    type="radio"
                    className="form-check-input"
                    id={id}
                    name="notificationPreference"
                    value={name}
                    checked={selectedPreference === name}
                    onChange={handleRadioChange}
                />
                <label className="form-check-label" htmlFor={id}>
                    {label}
                </label>
            </div>
        );
    };

    const formatPhoneDisplay = phone => {
        if (!phone) return '';
        try {
            return formatPhoneNumber(phone);
        } catch (e) {
            return phone;
        }
    };

    return (
        <div className="form-container p-3">
            <h2 className="iNGAGED-Typography-Heading3 mb-0">
                {getText('msg_8522df6dd7a3')}
            </h2>
            <small className="text-muted">
                {getText('msg_8531c2742e2b')}
            </small>
            <form onSubmit={handleSubmit}>
                <div
                    className={`text-left d-flex flex-column gap-4 px-3 my-4 pe-5 ${styles.notificationForm}`}
                >
                    {renderRadioButton(
                        'originalEmailNotification',
                        'originalEmail',
                        availableChannels.originalEmail,
                    )}
                    {renderRadioButton(
                        'modifiedEmailNotification',
                        'modifiedEmail',
                        availableChannels.modifiedEmail,
                    )}
                    {renderRadioButton(
                        'originalPhoneNotification',
                        'originalPhone',
                        formatPhoneDisplay(availableChannels.originalPhone),
                    )}
                    {renderRadioButton(
                        'modifiedPhoneNotification',
                        'modifiedPhone',
                        formatPhoneDisplay(availableChannels.modifiedPhone),
                    )}
                </div>

                <div className="form-group col-12 mx-auto text-start pt-3">
                    <small>
                        {getText('msg_19326dec1510')}
                    </small>
                </div>

                <div className="form-group d-grid gap-2 col-4 mx-auto text-center pt-4">
                    <button type="submit" className="ing-button-primary-left">
                        {getText('msg_bc981983e7f5')}
                        <i className="bi bi-chevron-right"></i>
                    </button>
                </div>
                <div className="form-group d-grid gap-2 col-10 mx-auto text-center pt-3">
                    <small>
                        <a
                            href={`/get-support${
                                companyCode ? `?companyCode=${companyCode}` : ''
                            }`}
                            className="pl-2"
                        >
                            {getText('msg_8a38384386fa')}
                        </a>
                    </small>
                </div>
            </form>
        </div>
    );
};

export default SetNotificationPreferencesForm;
